/**
 * Environment utility functions for handling different deployment environments
 */

/**
 * Get the base URL for the current environment
 * This ensures correct redirect URLs for auth flows in different environments
 */
export const getBaseURL = (): string => {
  // Check if we're in production
  if (import.meta.env.PROD) {
    // Use environment variable if available
    if (import.meta.env.VITE_APP_URL) {
      return import.meta.env.VITE_APP_URL;
    }
    
    // Fallback to known staging/production URLs
    const hostname = window.location.hostname;
    
    // Staging environment on Cloudflare Pages
    if (hostname.includes('nvgc-ai-assistant-frontend-staging.pages.dev')) {
      return `https://${hostname}`;
    }
    
    // Production environment
    if (hostname.includes('nvgc-ai-assistant.com') || hostname.includes('nvgc-assistant.com')) {
      return `https://${hostname}`;
    }
    
    // Default to current origin for other production deployments
    return window.location.origin;
  }
  
  // Development environment
  return 'http://localhost:5173';
};

/**
 * Get the auth callback URL for OAuth providers
 */
export const getAuthCallbackURL = (): string => {
  return `${getBaseURL()}/auth/callback`;
};

/**
 * Get the password reset redirect URL
 */
export const getPasswordResetURL = (): string => {
  return `${getBaseURL()}/reset-password`;
};

/**
 * Get the email confirmation redirect URL
 */
export const getEmailConfirmationURL = (): string => {
  return `${getBaseURL()}/auth/confirm`;
};

/**
 * Check if we're in staging environment
 */
export const isStaging = (): boolean => {
  return window.location.hostname.includes('staging') || 
         import.meta.env.VITE_ENVIRONMENT === 'staging';
};

/**
 * Check if we're in production environment
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD && !isStaging();
};

/**
 * Check if we're in development environment
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV;
};